import { useAppSelector } from '@frontend/common';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { formStore, initForm } from './form-slice';

interface Props<T> {
    dispatch: ThunkDispatch<any, any, Action>;
    initialValue?: T;
    id?: string;
}

export function useFormRepository<T>(props: Props<T>) {
    const [formId] = useState<string>(props.id ?? crypto.randomUUID());

    useEffect(() => {
        props.dispatch(initForm({ formId, value: props.initialValue }));
    }, [formId, props.initialValue]);

    return {
        formId
    };
}

/**
 * Hook to read form values directly from Redux store.
 * Use this instead of syncing form values back to parent component state.
 * This prevents unnecessary re-renders when form fields change.
 *
 * @param formId - The form ID (must be stable, use useMemo or useState to create it)
 * @returns The form values or undefined if form is not initialized
 */
export function useFormValues<T>(formId: string | null): T | null {
    const store = useAppSelector(useSelector, formStore);
    return useMemo(() => {
        if (!formId) return null;
        return store[formId]?.value as T | null;
    }, [store, formId]);
}

/**
 * Hook to read form values for multiple forms from Redux store.
 * Use when a component renders multiple Form instances and needs access to all their values.
 *
 * @param formIds - Array of form IDs to read values for
 * @returns A record mapping each form ID to its current values (or null if not initialized)
 */
export function useMultiFormValues<T>(formIds: string[]): Record<string, T | null> {
    const store = useAppSelector(useSelector, formStore);
    return useMemo(() => {
        const result: Record<string, T | null> = {};
        for (const id of formIds) {
            result[id] = (store[id]?.value as T | null) ?? null;
        }
        return result;
    }, [store, formIds]);
}
